import { Component } from '@theme/component';

/**
 * @typedef {Object} CollapsibleTextRefs
 * @property {HTMLDetailsElement} details - The disclosure wrapping the whole component
 * @property {HTMLElement} body - Everything below the summary; this is what animates
 * @property {HTMLElement} [teaser] - Optional plain-text opening shown on desktop while collapsed
 */

/**
 * A custom element that shows or hides its content in full — no clamped preview.
 * Collapsed, only the summary (the "expand" button) is visible; expanded, the whole
 * content plus a "collapse" button at the bottom.
 *
 * The open/closed state lives on the native `<details>`, so the component still works
 * without JS (the summary toggles it) and the content stays in the DOM for crawlers.
 * This class only adds what `<details>` can't do on its own: the bottom collapse button
 * and a height transition.
 *
 * @extends {Component<CollapsibleTextRefs>}
 */
class CollapsibleText extends Component {
  requiredRefs = ['details', 'body'];

  /** @type {Animation | undefined} */
  #animation;

  /** @constant {number} */
  #animationSpeed = 300;

  /**
   * Height of the teaser when the content was last expanded. The teaser is hidden
   * while open, so it can't be measured at collapse time; collapsing animates back
   * down to this value so the full text shrinks into the teaser it came from.
   * @type {number}
   */
  #teaserHeight = 0;

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.details.addEventListener('click', this.#onDetailsClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.details.removeEventListener('click', this.#onDetailsClick);
    this.#animation?.cancel();
  }

  /**
   * The native summary toggle snaps the content open and shut. Intercept it so the
   * body can animate instead, and let #expand/#collapse own the `open` attribute.
   *
   * Bound on the `<details>` rather than the `<summary>` so clicks on anything the
   * merchant nests inside the label still count.
   *
   * @param {MouseEvent} event
   */
  #onDetailsClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest('summary')) return;

    event.preventDefault();

    if (this.refs.details.open) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  /**
   * Collapses the content. Wired to the bottom button via `on:click`.
   * @param {Event} [event]
   */
  collapse = (event) => {
    event?.preventDefault();
    this.#collapse();
  };

  #expand = () => {
    const { details, body } = this.refs;
    if (details.open) return;

    // Measure the teaser before opening: `open` hides it (and it is `display: none` on
    // mobile, where this reads 0). Starting from its height makes the full text grow
    // out of the teaser instead of collapsing to nothing first.
    this.#teaserHeight = this.refs.teaser?.offsetHeight ?? 0;

    // Open first so the body is rendered and can be measured.
    details.open = true;

    if (this.#prefersReducedMotion) return;
    this.#animateHeight(this.#teaserHeight, body.scrollHeight);
  };

  #collapse = () => {
    const { details, body } = this.refs;
    if (!details.open) return;

    if (this.#prefersReducedMotion) {
      details.open = false;
      return;
    }

    // Closing `details` mid-animation would unrender the body, so the attribute is
    // dropped only once the height has finished travelling to zero.
    this.#animateHeight(body.scrollHeight, this.#teaserHeight, () => {
      details.open = false;
    });
  };

  /**
   * Animates the body's height between two values.
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} [onFinish]
   */
  #animateHeight = (startHeight, endHeight, onFinish) => {
    const { body } = this.refs;

    this.#animation?.cancel();
    body.style.overflow = 'hidden';

    this.#animation = body.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      body.style.removeProperty('overflow');
      onFinish?.();
    };
  };
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}

export default CollapsibleText;
